/**
 * useSuccessionTree.js
 *
 * Backend-driven data hooks for Succession Planning's org view. Replaces the
 * old mock-based useOrgTree (see useTalentMobilityData.js) — the tree here
 * comes straight from GET /talent-mobility/succession-tree with nested
 * `children` already resolved server-side, so no client-side tree building or
 * ancestor-filtering logic is needed.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { getSuccessionTreeFilters, getSuccessionTree } from "Api/apiService";

export const EMPTY_SUCCESSION_FILTERS = {
  legalEntity: [],
  businessUnit: [],
  function: [],
  subFunction: [],
  level: [],
};

const toOptions = (values = []) => values.map((value) => ({ label: value, value }));

// ─── Filter options (Legal Entity / Business Unit / Function / Sub-Function / Level) ──

export const useSuccessionTreeFilterOptions = () => {
  const [options, setOptions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const requestIdRef = useRef(0);

  const fetchOptions = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError(false);
    try {
      const data = await getSuccessionTreeFilters();
      if (requestId !== requestIdRef.current) return; // a newer request already resolved
      setOptions({
        legalEntity: toOptions(data?.legal_entities),
        businessUnit: toOptions(data?.business_units),
        function: toOptions(data?.functions),
        subFunction: toOptions(data?.sub_functions),
        level: toOptions(data?.levels),
      });
    } catch (err) {
      if (requestId !== requestIdRef.current) return;
      console.error("Error loading succession tree filter options:", err);
      setError(true);
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOptions();
  }, [fetchOptions]);

  return { options, loading, error, refetch: fetchOptions };
};

// ─── Succession tree (roots + warnings) for the applied filter set ────────────

export const useSuccessionTree = (appliedFilters) => {
  const [roots, setRoots] = useState([]);
  const [warnings, setWarnings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const requestIdRef = useRef(0);

  const fetchTree = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError(false);
    try {
      // No root_emp_id sent by default — the backend determines the correct
      // root itself (full org for a full-org user, the logged-in employee's
      // own subtree for a restricted user). Only pass rootEmpId when the user
      // has explicitly drilled into a specific node (no such interaction
      // exists in the UI today); getSuccessionTree already supports it via
      // params.rootEmpId whenever that's added.
      const data = await getSuccessionTree(appliedFilters);
      if (requestId !== requestIdRef.current) return; // stale response — a newer filter set already applied
      const nextRoots = data?.roots || [];
      const nextWarnings = data?.warnings || [];
      setRoots(nextRoots);
      setWarnings(nextWarnings);
      if (nextWarnings.length) {
        // Data-quality signals from the backend (e.g. missing_manager) — not a
        // frontend error, just logged for developers rather than shown inline.
        console.warn("Succession tree warnings:", nextWarnings);
      }
    } catch (err) {
      if (requestId !== requestIdRef.current) return;
      console.error("Error loading succession tree:", err);
      setError(true);
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  }, [appliedFilters]);

  useEffect(() => {
    fetchTree();
  }, [fetchTree]);

  return { roots, warnings, loading, error, refetch: fetchTree };
};
